import React, { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import SimpleParticle from "./SimpleParticle";

const getIcon = (tag) => {
  const commonProps = { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', className: 'flex-shrink-0' };
  switch (tag) {
    case 'Multi-Platform':
      return (
        <svg {...commonProps} xmlns="http://www.w3.org/2000/svg">
          <rect x="2" y="4" width="20" height="14" rx="2" stroke="#3C5DF7" strokeWidth="1.5" fill="none" />
          <circle cx="7" cy="11" r="1.2" fill="#3C5DF7" />
        </svg>
      );
    case 'Batch Delivery':
      return (
        <svg {...commonProps} xmlns="http://www.w3.org/2000/svg">
          <path d="M3 6h18" stroke="#3C5DF7" strokeWidth="1.5" strokeLinecap="round" />
          <path d="M3 12h18" stroke="#3C5DF7" strokeWidth="1.5" strokeLinecap="round" />
          <path d="M3 18h18" stroke="#3C5DF7" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      );
    case 'Quick':
      return (
        <svg {...commonProps} xmlns="http://www.w3.org/2000/svg">
          <path d="M2 12h14l-4-4M16 12l-4 4" stroke="#3C5DF7" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case 'Snappy Pacing':
      return (
        <svg {...commonProps} xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2v10l6 4" stroke="#3C5DF7" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case 'Viral-Ready':
      return (
        <svg {...commonProps} xmlns="http://www.w3.org/2000/svg">
          <path d="M4 12l6-6 6 6 4-4" stroke="#3C5DF7" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        </svg>
      );
    case 'Subtitled':
      return (
        <svg {...commonProps} xmlns="http://www.w3.org/2000/svg">
          <rect x="3" y="6" width="18" height="12" rx="2" stroke="#3C5DF7" strokeWidth="1.5" fill="none" />
          <path d="M7 10h10M7 14h6" stroke="#3C5DF7" strokeWidth="1.4" strokeLinecap="round" />
        </svg>
      );
    case 'Retention-Driven':
      return (
        <svg {...commonProps} xmlns="http://www.w3.org/2000/svg">
          <path d="M12 21s-6-4.35-6-8a6 6 0 0112 0c0 3.65-6 8-6 8z" stroke="#3C5DF7" strokeWidth="1.3" fill="none" />
        </svg>
      );
    case 'Click Magnet':
      return (
        <svg {...commonProps} xmlns="http://www.w3.org/2000/svg">
          <path d="M3 3l18 18" stroke="#3C5DF7" strokeWidth="1.6" strokeLinecap="round" />
          <path d="M10 3l11 11" stroke="#3C5DF7" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      );
    default:
      return (
        <svg {...commonProps} xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="5" fill="#3C5DF7" />
        </svg>
      );
  }
};

const Tag = ({ text }) => (
  <span className="flex items-center gap-2 bg-white/5 border border-white/10 text-white/90 px-3 py-1 rounded-full text-[22px] font-medium h-[37px] pl-[17px]">
    {getIcon(text)}
    <span className="text-[13px]">{text}</span>
  </span>
);

const ServicesSection = () => {
  // follower states for card 2 and card 4 (index-based within this layout)
  const [f2, setF2] = useState({ x: 0, y: 0, visible: false });
  const [f4, setF4] = useState({ x: 0, y: 0, visible: false });

  const handleMove = (e, which) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    if (which === 2) setF2({ x, y, visible: true });
    if (which === 4) setF4({ x, y, visible: true });
  };

  const handleLeave = (which) => {
    if (which === 2) setF2((s) => ({ ...s, visible: false }));
    if (which === 4) setF4((s) => ({ ...s, visible: false }));
  };

  // small requestAnimationFrame smoothing for better visuals
  const followerRef2 = useRef(null);
  const followerRef4 = useRef(null);

  useEffect(() => {
    if (followerRef2.current) {
      followerRef2.current.style.transform = `translate(${f2.x - 12}px, ${f2.y - 12}px) scale(${f2.visible ? 1 : 0.3})`;
      followerRef2.current.style.opacity = f2.visible ? '1' : '0';
    }
    if (followerRef4.current) {
      followerRef4.current.style.transform = `translate(${f4.x - 12}px, ${f4.y - 12}px) scale(${f4.visible ? 1 : 0.3})`;
      followerRef4.current.style.opacity = f4.visible ? '1' : '0';
    }
  }, [f2, f4]);

  return (
    <section id="services" className="py-16 md:py-28 px-4 md:px-6 bg-transparent pt-[40px] md:pt-[80px] pb-[0px] md:pb-[100px]">
      <div className="max-w-[1400px] mx-auto">

        {/* HEADER */}
        <motion.div
          className="text-center mb-12 md:mb-20 md:mb-[50px]"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-center mb-[15px] md:mb-[25px] justify-center gap-2 mb-6 border border-dashed border-neutral-300 shadow-sm h-[35px] w-[110px] mx-auto rounded-[40px] bg-white">
            <svg width="10" height="10" viewBox="0 0 256 256" fill="#3C5DF7" className="animate-spin" style={{ animationDuration: "5s" }}>
              <path d="M240,128a15.79,15.79,0,0,1-10.5,15l-63.44,23.07L143,229.5a16,16,0,0,1-30,0L89.94,166.06,26.5,143a16,16,0,0,1,0-30L89.94,89.94,113,26.5a16,16,0,0,1,30,0l23.07,63.44L229.5,113A15.79,15.79,0,0,1,240,128Z" />
            </svg>
            <span className="text-gray-700 font-medium text-[15px]">Services</span>
          </div>

          <h2 className="text-[36px] md:text-[50px] font-semibold leading-[1.05] text-[#171717] mb-3 md:mb-4 tracking-[-2px] md:tracking-[-3px]" style={{fontWeight: 700}}>
            What 
            <span style={{fontFamily: "Recoleta", color: "#3C5DF7"}}> We</span> 
            <span className="font-semibold"> Do</span>
            <span style={{fontFamily: "Recoleta", color: "#3C5DF7"}}> Best</span>
          </h2>

          <p className="text-center text-[15.5px] text-[#555] max-w-[500px] mt-[10px] mb-[47px] leading-[1.4] font-[600] mx-auto">
            We take care of all production stages, ensuring your content not only reaches your audience but also monetizes and goes viral.
          </p>
        </motion.div>

        {/* === ROW 1 === */}
        <div className="flex flex-col md:flex-row gap-6 mb-3 max-w-[950px] mx-auto">

          {/* --- CARD 1 - Large --- */}
          <motion.div
            className="bg-[#171717] text-white p-6 ml-0 md:ml-[27px] rounded-[20px] flex-1 min-h-[300px] relative overflow-hidden shadow-[0_10px_20px_rgba(0,0,0,0.15)]"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <SimpleParticle />

            <h3 className="text-[20px] font-semibold mb-3 pt-[5px]">YT 360 degree</h3>
            <p className="text-[#B4B4B4] text-[16px] leading-relaxed mb-6 max-w-[100%]">
              Our end-to-end service covers everything: SEO, upload optimisation, scheduling, community engagement, and moderation — all while saving you time.</p>
            <div className="absolute bottom-4 left-4 flex gap-2 flex-wrap">
              <Tag text="Title, description & tags optimization" />
              <Tag text="Channel optimization" />
            </div>
          </motion.div>

          {/* --- CARD 2 - Small --- */}
          <motion.div
            className="bg-[#171717] text-white p-6 rounded-[20px] ml-0 md:ml-[-15px] w-full md:w-[38%] min-h-[300px] mr-0 md:mr-[22px] relative overflow-hidden shadow-[0_10px_20px_rgba(0,0,0,0.15)]"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <SimpleParticle />

            <h3 className="text-[20px] font-semibold mb-3 pt-[5px]">Video editing</h3>
            <p className="text-[#B4B4B4] text-[16px] leading-relaxed mb-6 max-w-[100%]">
                From talking-head to real estate videos, we
                deliver clean, professional edits that elevate
                your brand and keep your audience engaged.
            </p>

            <div className="absolute bottom-4 left-4">
              <Tag text="Retention-Driven" />
            </div>

            {/* cursor follower for card 2 */}
            <div
              ref={followerRef2}
              style={{
                transition: 'transform 0.12s cubic-bezier(.2,.9,.2,1), opacity 0.12s',
                position: 'absolute',
                left: 0,
                top: 0,
                width: 24,
                height: 24,
                borderRadius: 9999,
                pointerEvents: 'none',
                background: 'radial-gradient(circle at 30% 30%, rgba(255,81,0,0.95), rgba(255,81,0,0.12))',
                transform: 'translate(-12px, -12px) scale(0.3)',
                opacity: 0,
                mixBlendMode: 'screen',
              }}
            />
          </motion.div>

        </div>

        {/* === ROW 2 === */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-[950px] mx-auto">

          {/* --- CARD 3 --- */}
          <motion.div
            className="bg-[#171717] text-white p-6 ml-0 md:ml-[27px] mr-0 md:mr-[75px] rounded-[20px] min-h-[300px] relative overflow-hidden shadow-[0_10px_20px_rgba(0,0,0,0.15)]"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <SimpleParticle />

            <h3 className="text-[20px] font-semibold mb-3 pt-[5px]">Thumbnail Design</h3>
            <p className="text-[#B4B4B4] text-[16px] leading-relaxed mb-6 max-w-[100%]">
                We design attention-grabbing thumbnails
                based on in-depth topic research and A/B
                testing to help your videos get more clicks and
                views.            
              </p>

            <div className="absolute bottom-4 left-4">
              <Tag text="Clickbait" />
            </div>
          </motion.div>

          {/* --- CARD 4 --- */}
          <motion.div
            className="bg-[#171717] text-white p-6 ml-0 md:ml-[-90px] mr-0 md:mr-[20px] rounded-[20px] min-h-[300px] relative overflow-hidden shadow-[0_10px_20px_rgba(0,0,0,0.15)]"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <SimpleParticle />

            <h3 className="text-[20px] font-semibold mb-3 pt-[5px]">Podcast Production</h3>
            <p className="text-[#B4B4B4] text-[16px] leading-relaxed mb-6 max-w-[100%]">
               We edit podcasts of any setup and provide end-to-end support from scripting and multi-camera editing to distribution — delivering polished, ready-to-publish episodes.

            </p>

            <div className="absolute bottom-4 left-4 flex gap-2 flex-wrap">
              <Tag text="Full production" />
              <Tag text="Monetization support" />
            </div>

            {/* cursor follower for card 4 */}
            <div
              ref={followerRef4}
              style={{
                transition: 'transform 0.12s cubic-bezier(.2,.9,.2,1), opacity 0.12s',
                position: 'absolute',
                left: 0,
                top: 0,
                width: 24,
                height: 24,
                borderRadius: 9999,
                pointerEvents: 'none',
                background: 'radial-gradient(circle at 30% 30%, rgba(255,81,0,0.95), rgba(255,81,0,0.12))',
                transform: 'translate(-12px, -12px) scale(0.3)',
                opacity: 0,
                mixBlendMode: 'screen',
              }}
            />
          </motion.div>

        </div>

      </div>
    </section>
  );
};

export default ServicesSection;
